import { useEffect, useState, useCallback } from 'react';

import { extractFormFieldsFromAnnotations } from './usePDFFormFields';
import { logger } from '../utils/logger';

import type { FormField } from '../types/pdf.types';

type PDFDocumentProxyLike = any;

interface UsePDFFieldMappingsParams {
  pdfDocument: PDFDocumentProxyLike | null;
}

export const usePDFFieldMappings = ({
  pdfDocument,
}: UsePDFFieldMappingsParams) => {
  const [fieldNameMapping, setFieldNameMapping] = useState<Map<string, string>>(
    new Map(),
  );
  const [fieldTypeMapping, setFieldTypeMapping] = useState<Map<string, string>>(
    new Map(),
  );
  const [fieldNoExportMapping, setFieldNoExportMapping] = useState<
    Map<string, boolean>
  >(new Map());
  const [fieldRadioGroupMapping, setFieldRadioGroupMapping] = useState<
    Map<string, string>
  >(new Map());
  const [fieldExportValueMapping, setFieldExportValueMapping] = useState<
    Map<string, string>
  >(new Map());

  useEffect(() => {
    if (!pdfDocument || !pdfDocument.numPages) return;
    let cancelled = false;

    const preloadFieldMappings = async () => {
      try {
        const allFields: FormField[] = [];
        for (
          let pageNumber = 1;
          pageNumber <= pdfDocument.numPages;
          pageNumber++
        ) {
          const page = await pdfDocument.getPage(pageNumber);
          const annotations = await page.getAnnotations();
          const baseViewport = page.getViewport({ scale: 1.0 });
          const fields = extractFormFieldsFromAnnotations(
            annotations,
            pageNumber,
            baseViewport.height,
          );
          allFields.push(...fields);
        }

        if (cancelled || allFields.length === 0) return;

        setFieldNameMapping((prev) => {
          const updated = new Map(prev);
          let changed = false;
          allFields.forEach((field) => {
            if (
              !updated.has(field.id) ||
              updated.get(field.id) !== field.name
            ) {
              updated.set(field.id, field.name);
              changed = true;
            }
          });
          return changed ? updated : prev;
        });

        setFieldTypeMapping((prev) => {
          const updated = new Map(prev);
          let changed = false;
          allFields.forEach((field) => {
            if (
              !updated.has(field.id) ||
              updated.get(field.id) !== field.type
            ) {
              updated.set(field.id, field.type);
              changed = true;
            }
          });
          return changed ? updated : prev;
        });

        setFieldNoExportMapping((prev) => {
          const updated = new Map(prev);
          let changed = false;
          allFields.forEach((field) => {
            if (
              typeof field.noExport === 'boolean' &&
              (!updated.has(field.id) ||
                updated.get(field.id) !== field.noExport)
            ) {
              updated.set(field.id, field.noExport);
              changed = true;
            }
          });
          return changed ? updated : prev;
        });

        setFieldExportValueMapping((prev) => {
          const updated = new Map(prev);
          let changed = false;
          allFields.forEach((field) => {
            if (
              field.exportValue &&
              (!updated.has(field.id) ||
                updated.get(field.id) !== field.exportValue)
            ) {
              updated.set(field.id, field.exportValue);
              changed = true;
            }
          });
          return changed ? updated : prev;
        });
      } catch (error) {
        logger.warn('[PDFEditor] Failed to preload field mappings:', error);
      }
    };

    preloadFieldMappings();
    return () => {
      cancelled = true;
    };
  }, [pdfDocument]);

  const handleFieldsDiscovered = useCallback(
    (
      fields: Array<{
        id: string;
        name: string;
        type: string;
        fontSize?: number;
        readOnly?: boolean;
        required?: boolean;
        noExport?: boolean;
        radioGroup?: string;
        exportValue?: string;
      }>,
    ) => {
      logger.debug(`[PDFEditor] Registering ${fields.length} fields`);
      setFieldNameMapping((prev) => {
        const updated = new Map(prev);
        let changed = false;
        fields.forEach((f) => {
          if (!prev.has(f.id) || prev.get(f.id) !== f.name) {
            updated.set(f.id, f.name);
            changed = true;
          }
        });
        if (changed) {
          logger.debug(
            `[PDFEditor] Field mapping updated, total fields: ${updated.size}`,
          );
        }
        return changed ? updated : prev;
      });

      setFieldTypeMapping((prev) => {
        const updated = new Map(prev);
        let changed = false;
        fields.forEach((f) => {
          if (f.type && (!prev.has(f.id) || prev.get(f.id) !== f.type)) {
            updated.set(f.id, f.type);
            changed = true;
          }
        });
        return changed ? updated : prev;
      });

      setFieldNoExportMapping((prev) => {
        const updated = new Map(prev);
        let changed = false;
        fields.forEach((f) => {
          if (
            typeof f.noExport === 'boolean' &&
            (!prev.has(f.id) || updated.get(f.id) !== f.noExport)
          ) {
            updated.set(f.id, f.noExport);
            changed = true;
          }
        });
        return changed ? updated : prev;
      });

      setFieldRadioGroupMapping((prev) => {
        const updated = new Map(prev);
        let changed = false;
        fields.forEach((f) => {
          if (
            f.radioGroup &&
            (!prev.has(f.id) || prev.get(f.id) !== f.radioGroup)
          ) {
            updated.set(f.id, f.radioGroup);
            changed = true;
          }
        });
        return changed ? updated : prev;
      });

      setFieldExportValueMapping((prev) => {
        const updated = new Map(prev);
        let changed = false;
        fields.forEach((f) => {
          if (
            f.exportValue &&
            (!prev.has(f.id) || prev.get(f.id) !== f.exportValue)
          ) {
            updated.set(f.id, f.exportValue);
            changed = true;
          }
        });
        return changed ? updated : prev;
      });
    },
    [],
  );

  return {
    fieldNameMapping,
    fieldTypeMapping,
    fieldNoExportMapping,
    fieldRadioGroupMapping,
    fieldExportValueMapping,
    handleFieldsDiscovered,
  };
};
